import { lazy, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Nav from './Nav';
import HeroSection from './sections/HeroSection';

import PageHead from '../../seo/PageHead';
import { organizationSchema } from '../../seo/jsonld/organization';
import { websiteSchema } from '../../seo/jsonld/website';
import { softwareApplicationSchema } from '../../seo/jsonld/softwareApplication';
import { buildFaqSchema } from '../../seo/jsonld/faqPage';
import { buildBreadcrumbForRoute } from '../../seo/jsonld/breadcrumbForRoute';
import { comparisonItemList } from '../../seo/jsonld/comparison';

import { useLanguage } from '../../app/providers/LanguageProvider';

import './Home.css';

// Everything below the hero is in a lazy chunk. The hero itself stays in
// main (statically imported above) so React manages it from initial mount —
// no post-hydration re-paint, no lazy-chunk LCP re-fire. Mobile LCP was
// previously capped at ~4s because of that re-fire.
const LandingBelowHero = lazy(() => import('./LandingBelowHero'));

const Home = () => {
  const { t } = useTranslation();
  const { language, direction } = useLanguage();
  const isArabic = language === 'ar';

  // Re-derive when language changes so the prerendered AR FAQ schema
  // is in Arabic. buildFaqSchema reads i18n synchronously and i18n's active
  // language is what's stale on first /ar/ render, so we depend on language.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const faqSchema = useMemo(() => buildFaqSchema(), [language]);
  const breadcrumbSchema = useMemo(
    () => buildBreadcrumbForRoute('/', { isArabic }),
    [isArabic],
  );
  const schemas = useMemo(
    () =>
      [
        organizationSchema,
        websiteSchema,
        softwareApplicationSchema,
        faqSchema,
        breadcrumbSchema,
        comparisonItemList,
      ].filter(Boolean),
    [faqSchema, breadcrumbSchema],
  );

  return (
    <>
      <PageHead
        titleEn="Raskh — Make What You Learn Stay Learned"
        titleAr="راسخ — رسّخ ما تتعلمه"
        descEn={t(
          'landing.hero.subheadV3',
          'Upload a PDF. Raskh turns it into flashcards, mind maps, a podcast, and a Feynman voice tutor that listens to you teach it back — in Arabic and English, with no streaks and no shame.',
        )}
        descAr="ارفع ملف PDF. يحوّله رسخ إلى بطاقات وخريطة ذهنية وبودكاست ومعلّم فاينمان صوتي يستمع إليك وأنت تشرحه — بالعربية والإنجليزية، بلا سلاسل ولا خجل."
        canonical={isArabic ? 'https://raskh.app/ar/' : 'https://raskh.app/'}
        hreflangEn="https://raskh.app/"
        hreflangAr="https://raskh.app/ar/"
        schemas={schemas}
        lang={language}
      />

      <div className="home-page-container" dir={direction}>
        <Nav />
        <main id="main" role="main">
          <article>
            <HeroSection />
            <Suspense fallback={null}>
              <LandingBelowHero />
            </Suspense>
          </article>
        </main>
      </div>
    </>
  );
};

export default Home;
